import { formatCents } from '@affily/shared/money';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { Spinner, SectionTitle, Alert } from '../../components/ui.jsx';

const pct = (bps) => `${(bps / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

/** €100 at a rate, as an example a person can check in their head. */
const example = (bps) => Math.round((10_000 * bps) / 10_000);

function RateTable({ rates, side }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
      <table className="data-table">
        <thead>
          <tr>
            <th>What the commission is for</th>
            <th className="num">{side === 'brand' ? 'Added to what you pay' : 'Taken from your commission'}</th>
            <th className="num">On a €100.00 commission</th>
          </tr>
        </thead>
        <tbody>
          {rates.map((r) => (
            <tr key={r.kind}>
              <td>{r.label}</td>
              <td className="num font-medium">{pct(r.bps)}</td>
              <td className="num text-ink-muted">
                {side === 'brand'
                  ? `you pay ${formatCents(example(r.bps), { currency: 'eur' })}`
                  : `you receive ${formatCents(10_000 - example(r.bps), { currency: 'eur' })}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * What Affily charges this account. Fees aren't published: a creator sees what
 * comes out of their commissions, a brand what is added on top of what it pays.
 */
export default function Pricing() {
  const { isBrand } = useAuth();
  const { data, loading, error } = useFetch('/pricing');

  return (
    <DashboardLayout
      side={isBrand ? 'brand' : 'creator'}
      title="Pricing"
      description="Free to join. Affily charges only on commissions it tracked and paid out."
    >
      <div className="max-w-3xl space-y-8">
        {loading ? (
          <Spinner />
        ) : error ? (
          <Alert>{error.message}</Alert>
        ) : (
          <>
            {data.creator && (
              <section>
                <SectionTitle hint="Taken once, when a commission accrues, and shown on every commission line">
                  As a creator
                </SectionTitle>
                <RateTable rates={data.creator.rates} side="creator" />
                <p className="mt-2 text-xs text-ink-faint">
                  The rate depends on what the program pays for, and is fixed when you join it. Nothing else is
                  charged: no listing fee, no subscription, no fee on deals Affily didn’t track.
                </p>
              </section>
            )}
            {data.brand && (
              <section>
                <SectionTitle hint="Added on top of each commission when you pay a creator">As a brand</SectionTitle>
                <RateTable rates={data.brand.rates} side="brand" />
                <dl className="mt-4 grid gap-3 rounded-lg border border-rule bg-paper-raised p-4 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="font-medium">Paying by card</dt>
                    <dd className="text-ink-muted">
                      Adds {pct(data.brand.cardFee.bps)} plus {(data.brand.cardFee.fixedCents / 100).toFixed(2)} in the
                      payment’s currency, shown before you pay.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium">Paying by bank transfer</dt>
                    <dd className="text-ink-muted">
                      No card fee. For {formatCents(data.brand.bankTransfer.minCents, { currency: 'eur', dropZeroCents: true })} or
                      more, in {data.brand.bankTransfer.currencies.map((c) => c.toUpperCase()).join(', ')}, from the Settle page.
                    </dd>
                  </div>
                </dl>
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
